import { z } from "zod";
import { Label } from "components/label";
import { Controller, useForm } from "react-hook-form";
import { HttpError, useOne } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { SwitchButton } from "components/switchButton";
import Input from "components/input";
import Button from "components/button";
import { Link } from "react-router-dom";
import Select from "components/select";
import {
  IAdminAccountDetail,
  IAdminAccountForm,
  IOrgEventIdsItem,
  IOrgIdsItem,
} from "interfaces";
import {
  ALL_OPTION,
  PHONE_CODE,
  REGEX_PHONE_NUMBER,
  REGEX_VALID_EMAIL,
} from "utills/constants";
import { arrayCompare } from "utills/helpers";
import { useState } from "react";
import { isEmpty } from "lodash";
import { ChevronRightIcon } from "@heroicons/react/outline";

export interface AdmninAccountFormProps {
  id?: string;
  defaultData?: IAdminAccountDetail;
  onSubmit: (data: IAdminAccountForm) => void;
}

const AdminAccountSchema = z.object({
  email: z
    .string()
    .min(1, "This field is required")
    .refine(
      (value) => REGEX_VALID_EMAIL.test(value ?? ""),
      "Please enter a valid email"
    ),
  first_name: z.string().min(1, "This field is required"),
  last_name: z.string().min(1, "This field is required"),
  role_id: z.string().min(1, "This field is required"),
  phone_area_code: z.string(),
  phone: z.union([
    z.literal(""),
    z
      .string()
      .refine(
        (value) => REGEX_PHONE_NUMBER.test(value ?? ""),
        "Invalid phone number"
      ),
  ]),
  is_active: z.boolean(),
  organization_ids: z.array(z.string()).min(1, "This field is required"),
  organization_mapping: z.record(
    z.string(),
    z.array(z.string()).min(1, "This field is required")
  ),
});

export const AdminAccountForm = ({
  id,
  defaultData,
  onSubmit,
}: AdmninAccountFormProps) => {
  const [selectedEventOrgId, setSelectedEventOrgId] = useState("");
  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IAdminAccountForm, HttpError, IAdminAccountForm>({
    defaultValues: {
      email: defaultData?.email ?? "",
      first_name: defaultData?.first_name ?? "",
      is_active: defaultData?.is_active ?? false,
      last_name: defaultData?.last_name ?? "",
      organization_ids: defaultData?.organization_ids ?? [],
      organization_mapping: defaultData?.organization_mapping ?? {},
      phone: defaultData?.phone ?? "",
      phone_area_code: defaultData?.phone_area_code ?? "",
      role_id: defaultData?.role_id ?? "",
    },
    resolver: zodResolver(AdminAccountSchema),
  });

  const { data: eventIdsData, isFetching: isFetchingEventIds } = useOne<
    IOrgEventIdsItem[]
  >({
    resource: "content/dropdown/events?uncheck=true",
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: true,
    } as any,
  });

  const { data: organizationIdsData, isFetching: isFetchingOrgIds } = useOne<
    IOrgIdsItem[],
    HttpError
  >({
    resource: "organization",
    id: "ids",
    meta: {
      resource: "data",
    },

    queryOptions: {
      enabled: true,
    } as any,
  });

  const { data: roleData, isFetching: isFetchingRole } = useOne<
    any[],
    HttpError
  >({
    resource: "admin-account/role/all/ids?include_inactive=true",
    id: "",
    meta: {
      resource: "data",
    },
  });

  const getSelectedLabels = (selectedId: string) => {
    const item = organizationIds.find((item) => item.value === selectedId);
    return item ? item.label : null;
  };

  const _renderOrganizations = () => {
    const organizationArr = arrayCompare(watch("organization_ids"), [
      ALL_OPTION.value,
    ])
      ? organizationIds
          .filter((org) => org.value !== ALL_OPTION.value)
          .map((org) => org.value)
      : watch("organization_ids") ?? [];

    const getSelectedEventLength = (id: string) => {
      return watch("organization_mapping").hasOwnProperty(id)
        ? (watch("organization_mapping")[id] ?? []).length
        : "All";
    };

    return (
      <div
        className={`relative rounded-md ring-2 overflow-hidden shadow-md ${!isEmpty(errors["organization_mapping"]) ? "ring-red-500" : "ring-gray-100"}`}
      >
        <div className="flex flex-col gap-y-2 px-4 py-4 max-h-80 overflow-y-auto text-sm">
          {organizationArr.map((id) => (
            <button
              key={id}
              className={`flex items-center justify-between w-full text-left rounded-md px-4 py-3 ${
                errors["organization_mapping"]?.[id]
                  ? "bg-red-100 text-red-500 hover:bg-red-100"
                  : selectedEventOrgId === id
                  ? "bg-magnolia-500 hover:bg-magnolia-500"
                  : ""
              } hover:bg-magnolia-400`}
              onClick={() => setSelectedEventOrgId(id)}
            >
              <p>
                {getSelectedLabels(id)}{" "}
                <span className="font-medium">
                  ({getSelectedEventLength(id)})
                </span>
              </p>
              <ChevronRightIcon className="size-4 text-brand-500" />
            </button>
          ))}
        </div>
      </div>
    );
  };

  const _handleChangeEventMapping = (
    selected: { label: string; value: string }[]
  ) => {
    if (selected.find((item) => item.value === ALL_OPTION.value)) {
      _removeOrgFromMapping(selectedEventOrgId);
    } else {
      let updatedOrganizationMapping = watch("organization_mapping");
      updatedOrganizationMapping[selectedEventOrgId] = isEmpty(selected)
        ? []
        : selected.map((item) => `${item.value}`);
      setValue("organization_mapping", updatedOrganizationMapping, {
        shouldValidate: true,
      });
    }
  };

  const _removeOrgFromMapping = (id: string) => {
    if (id === ALL_OPTION.value) {
      setValue(
        "organization_mapping",
        {},
        {
          shouldValidate: true,
        }
      );
    } else {
      let updatedOrganizationMapping = watch("organization_mapping");
      delete updatedOrganizationMapping[id];
      setValue("organization_mapping", updatedOrganizationMapping, {
        shouldValidate: true,
      });
    }
  };

  const _submit = (values: IAdminAccountForm) => {
    const submitValues = { ...values };
    onSubmit(submitValues);
  };

  const organizationIds = (
    (organizationIdsData?.data ?? []).length > 0 ? [ALL_OPTION] : []
  ).concat(
    (organizationIdsData?.data ?? []).map((org: IOrgIdsItem) => ({
      value: org.id,
      label: org.name_en,
    }))
  );

  const roleIds = (roleData?.data ?? []).map((role: any) => ({
    value: role.id,
    label: role.name,
  }));

  const organizationEventIds = (
    (eventIdsData?.data ?? []).length > 0 ? [ALL_OPTION] : []
  ).concat(
    (eventIdsData?.data ?? [])
      .filter((event) => event.organization_id === selectedEventOrgId)
      .map((event: IOrgEventIdsItem) => ({
        organization_id: event.organization_id,
        value: event.id,
        label: event.name_en,
      }))
  );

  return (
    <div className="w-full ">
      <div>
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          <div className="w-full box-container">
            <div className="grid gap-x-4 gap-y-4 text-sm sm:grid-cols-8 lg:gap-x-6">
              <div className="sm:col-span-2">
                <Label required id="is_active" name="Status" />
                <Controller
                  control={control}
                  name="is_active"
                  defaultValue={true}
                  render={({ field: { onChange, value = true, ref } }) => (
                    <SwitchButton checked={value} onChange={onChange} />
                  )}
                />
              </div>
              <div className="sm:col-span-2">
                <Label id="email" name="Email" required />
                <Input
                  id="email"
                  type="text"
                  placeholder="Email"
                  error={errors["email"]}
                  {...register("email")}
                />
                {errors["email"] ? (
                  <p className="error-text">
                    <span className="font-medium">{errors?.email.message}</span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-2">
                <Label id="first_name" name="First name" required />
                <Input
                  id="first_name"
                  type="text"
                  placeholder="First name"
                  error={errors["first_name"]}
                  {...register("first_name")}
                />
                {errors["first_name"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.first_name.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-2">
                <Label id="last_name" name="Last name" required />
                <Input
                  id="last_name"
                  type="text"
                  placeholder="Last name"
                  error={errors["last_name"]}
                  {...register("last_name")}
                />
                {errors["last_name"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.last_name.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-2">
                <Label id="role_id" name="Role name" required />
                <Controller
                  control={control}
                  name="role_id"
                  render={({ field: { onChange, value = "", ref } }) => {
                    return (
                      <Select
                        key="role_id"
                        inputRef={ref}
                        options={roleIds}
                        id="role_id"
                        placeholder="Select role name"
                        isLoading={isFetchingRole}
                        defaultValue={
                          roleIds.find(
                            (selected: { label: string; value: any }) =>
                              selected.value === value
                          ) ?? ""
                        }
                        value={roleIds.find(
                          (selected: { label: string; value: any }) =>
                            selected.value === value
                        )}
                        onChange={(selected: { label: string; value: any }) => {
                          onChange(selected?.value);
                        }}
                        error={errors["role_id"]}
                      />
                    );
                  }}
                />
                {errors["role_id"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors?.role_id.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="sm:col-span-2">
                <Label id="phone" name="Contact Phone" />
                <div className="flex">
                  <Controller
                    control={control}
                    name="phone_area_code"
                    defaultValue="84"
                    render={({ field: { onChange, value = "", ref } }) => {
                      const areaPhoneCodeArr = PHONE_CODE.map((item) => ({
                        label: item.label,
                        value: item.code,
                      }));
                      return (
                        <Select
                          ref={ref}
                          className="flex-1 w-[160px] !rounded-none inline-flex items-center !rounded-l-md"
                          options={areaPhoneCodeArr}
                          id="phone_area_code"
                          value={areaPhoneCodeArr.find(
                            (c) => c.value === value
                          )}
                          onChange={(selected: {
                            label: string;
                            value: string;
                          }) => onChange(selected?.value)}
                          error={errors["phone_area_code"]}
                        />
                      );
                    }}
                  />
                  <Input
                    id="phone"
                    type="phone"
                    className={`${
                      errors["phone"]
                        ? "border-l-[1px] focus:!ml-0"
                        : "border-l-0"
                    } rounded-l-none focus:border-l-[1px] focus:ml-[-1px] z-[2]`}
                    placeholder="Phone number"
                    error={errors["phone"]}
                    {...register("phone")}
                  />
                </div>
                {errors["phone"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["phone"]?.message}
                    </span>
                  </p>
                ) : null}
              </div>
              <div className="col-span-full">
                <Label required id="organization_ids" name="Org name" />
                <Controller
                  control={control}
                  name="organization_ids"
                  defaultValue={[]}
                  render={({ field: { onChange, value = [], ref } }) => (
                    <Select
                      isMulti
                      key="organization_ids"
                      inputRef={ref}
                      options={
                        value.includes(ALL_OPTION.value) ? [] : organizationIds
                      }
                      id="organization_ids"
                      placeholder="Choose organization"
                      value={value.map((id: string) => {
                        return organizationIds.find(
                          (item) => item.value === id
                        );
                      })}
                      onChange={(
                        selected: { label: string; value: string }[],
                        actionMeta: any
                      ) => {
                        if (actionMeta.action === "remove-value") {
                          _removeOrgFromMapping(
                            actionMeta?.removedValue?.value ?? ""
                          );
                        }
                        selected.find((item) => item.value === ALL_OPTION.value)
                          ? onChange([ALL_OPTION.value])
                          : onChange(selected.map((item) => `${item.value}`));
                        setSelectedEventOrgId("");
                      }}
                      error={errors["organization_ids"]}
                      isLoading={isFetchingOrgIds}
                    />
                  )}
                />
                {errors["organization_ids"] ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {errors["organization_ids"]?.message}
                    </span>
                  </p>
                ) : null}
              </div>
              {!isEmpty(watch("organization_ids")) ? (
                <div className="sm:col-span-4">
                  <Label id="selected_orgnization_ids" name="Selected Org" />
                  {_renderOrganizations()}
                </div>
              ) : (
                <></>
              )}
              {selectedEventOrgId ? (
                <div className="sm:col-span-4">
                  <Label id="organization_mapping" name="Event name" />
                  <Select
                    isMulti
                    key="organization_mapping"
                    options={
                      !watch("organization_mapping").hasOwnProperty(
                        selectedEventOrgId
                      )
                        ? []
                        : organizationEventIds
                    }
                    id="organization_mapping"
                    placeholder="Choose event"
                    isLoading={isFetchingEventIds}
                    error={errors["organization_mapping"]?.[selectedEventOrgId]}
                    value={
                      !watch("organization_mapping").hasOwnProperty(
                        selectedEventOrgId
                      )
                        ? [ALL_OPTION]
                        : (
                            watch("organization_mapping")[selectedEventOrgId] ??
                            []
                          ).map((id: string) => {
                            return organizationEventIds.find(
                              (item) => item.value === id
                            );
                          })
                    }
                    onChange={(
                      selected: { label: string; value: string }[]
                    ) => {
                      _handleChangeEventMapping(selected);
                    }}
                  />
                  {errors["organization_mapping"]?.[selectedEventOrgId] ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {
                          errors["organization_mapping"]?.[selectedEventOrgId]
                            ?.message
                        }
                      </span>
                    </p>
                  ) : null}
                </div>
              ) : (
                <></>
              )}
            </div>
          </div>
          <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
            <div className="flex items-center justify-end gap-x-4">
              <Link
                to={`/admin-accounts/accounts${
                  defaultData?.id ? `/${defaultData?.id}` : ""
                }`}
              >
                <Button className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100">
                  Cancel
                </Button>
              </Link>
              <Button
                className="w-20"
                type="submit"
                onClick={handleSubmit(_submit)}
              >
                Save
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
